import { useEffect } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { Footer } from "@/sections/Footer";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const Particles = () => (
  <div className="absolute inset-0 overflow-hidden">
    {[...Array(50)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-1 h-1 bg-cyan-400 rounded-full blur-sm"
        initial={{ x: Math.random() * 100 + "%", y: Math.random() * 100 + "%" }}
        animate={{
          y: [0, -100, 0],
          opacity: [0, Math.random() * 0.5 + 0.3, 0],
        }}
        transition={{
          duration: Math.random() * 3 + 3,
          repeat: Infinity,
          ease: "linear",
        }}
      />
    ))}
  </div>
);

function Section1() {
  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-[#05070D] via-[#0a1628] to-[#05070D] overflow-hidden py-20 px-4">
      <Particles />
      <motion.div
        className="relative z-10 max-w-4xl text-center"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
      >
        <motion.div variants={itemVariants} className="mb-6">
          <span className="text-cyan-400 text-sm font-semibold tracking-widest">
            INOVAÇÃO · AUTOMAÇÃO · RESULTADOS
          </span>
        </motion.div>
        <motion.h1
          variants={itemVariants}
          className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-cyan-300 to-blue-400 bg-clip-text text-transparent"
        >
          Tecnologia que Transforma.
        </motion.h1>
        <motion.p
          variants={itemVariants}
          className="text-xl text-slate-300 mb-8 leading-relaxed max-w-2xl mx-auto"
        >
          Descubra como a integração inteligente entre ferramentas e automação pode revolucionar sua operação.
        </motion.p>
        <motion.div variants={itemVariants} className="flex gap-4 justify-center flex-wrap">
          <button className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all">
            Explorar
          </button>
          <button className="px-8 py-3 border border-cyan-400 text-cyan-400 rounded-full font-semibold hover:bg-cyan-400/10 transition-all">
            Saiba Mais
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}

function Section2() {
  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-[#05070D] to-[#0a1628] overflow-hidden py-20 px-4">
      <Particles />
      <div className="relative z-10 max-w-6xl w-full">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
          >
            <motion.div variants={itemVariants} className="mb-6">
              <span className="text-cyan-400 text-sm font-semibold tracking-widest">02 · SOLUÇÃO</span>
            </motion.div>
            <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold mb-6">
              Integração Perfeita de Ferramentas
            </motion.h2>
            <motion.p variants={itemVariants} className="text-slate-300 text-lg mb-6">
              Conecte suas plataformas favoritas em um único ecossistema. Sem limitações, sem barreiras.
            </motion.p>
            <motion.div variants={itemVariants} className="space-y-4">
              {["Sincronização em Tempo Real", "Zero Downtime", "Segurança de Ponta a Ponta"].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full" />
                  <span className="text-slate-200">{item}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="relative h-96 md:h-full min-h-96"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
          >
            <motion.div
              variants={itemVariants}
              className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl backdrop-blur-sm border border-cyan-500/30 flex items-center justify-center"
            >
              <div className="text-center">
                <div className="text-6xl mb-4">📱</div>
                <p className="text-slate-300">Dashboard Inteligente</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Section3() {
  const features = [
    { icon: "⚡", title: "Rápido", desc: "Processamento instantâneo" },
    { icon: "🔒", title: "Seguro", desc: "Criptografia de ponta a ponta" },
    { icon: "📊", title: "Analítico", desc: "Insights em tempo real" },
    { icon: "🚀", title: "Escalável", desc: "Cresce com seu negócio" },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-[#0a1628] to-[#05070D] overflow-hidden py-20 px-4">
      <Particles />
      <div className="relative z-10 max-w-6xl w-full">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          className="text-center mb-16"
        >
          <motion.span variants={itemVariants} className="text-cyan-400 text-sm font-semibold tracking-widest block mb-4">
            03 · RECURSOS
          </motion.span>
          <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold mb-6">
            Tudo que Você Precisa
          </motion.h2>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {features.map((feature, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              className="p-6 rounded-xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 hover:border-cyan-400 hover:shadow-lg hover:shadow-cyan-500/20 transition-all"
            >
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-slate-400 text-sm">{feature.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-[#05070D] via-[#0a1628] to-[#05070D] overflow-hidden py-20 px-4">
      <Particles />
      <motion.div
        className="relative z-10 max-w-4xl text-center"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
      >
        <motion.h2 variants={itemVariants} className="text-4xl md:text-6xl font-bold mb-6">
          Pronto para Começar?
        </motion.h2>
        <motion.p variants={itemVariants} className="text-xl text-slate-300 mb-8">
          Entre em contato conosco e descubra como podemos transformar sua operação.
        </motion.p>
        <motion.div variants={itemVariants} className="flex gap-4 justify-center flex-wrap">
          <button className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-cyan-500/50 transition-all">
            💬 Falar no WhatsApp
          </button>
          <button className="px-8 py-4 border-2 border-cyan-400 text-cyan-400 rounded-full font-semibold text-lg hover:bg-cyan-400/10 transition-all">
            ← Voltar ao Site
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}

export function Page2() {
  useEffect(() => {
    try {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
      let raf;
      const loop = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      return () => {
        cancelAnimationFrame(raf);
        lenis.destroy();
      };
    } catch (err) {
      console.error("Lenis error:", err);
    }
  }, []);

  return (
    <div className="App min-h-screen bg-[#05070D] font-sora text-slate-200 antialiased">
      <div className="relative z-10">
        <Navbar />
        <main>
          <Section1 />
          <Section2 />
          <Section3 />
          <CTA />
        </main>
        <Footer />
        <FloatingWhatsApp />
      </div>
    </div>
  );
}
